import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import './Breadcrumb.css';

interface BreadcrumbItem {
  label: string;
  path: string;
  isActive: boolean;
}

const Breadcrumb: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always start with home
    breadcrumbs.push({
      label: t('breadcrumb.home'),
      path: '/',
      isActive: location.pathname === '/',
    });

    // Generate breadcrumbs for each path segment
    let currentPath = '';
    pathnames.forEach((pathname, index) => {
      currentPath += `/${pathname}`;
      const isLast = index === pathnames.length - 1;
      
      const labelMap: Record<string, string> = {
        'open-source': t('nav.openSource'),
        'activity-heatmap-calendar': t(
          'openSource.packages.activityHeatmapCalendar.name',
          'Activity Heatmap Calendar'
        ),
      };

      const label =
        labelMap[pathname] ||
        pathname
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

      breadcrumbs.push({
        label,
        path: currentPath,
        isActive: isLast,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumb on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <div className="breadcrumb-container">
        <ol className="breadcrumb-list">
          {breadcrumbs.map((breadcrumb, index) => (
            <li key={breadcrumb.path} className="breadcrumb-item">
              {breadcrumb.isActive ? (
                <span className="breadcrumb-current" aria-current="page">
                  {breadcrumb.label}
                </span>
              ) : (
                <Link to={breadcrumb.path} className="breadcrumb-link">
                  {breadcrumb.label}
                </Link>
              )}
              {index < breadcrumbs.length - 1 && (
                <span className="breadcrumb-separator" aria-hidden="true">
                  ›
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;
